'use client';

import React from 'react';
import {
  Fuel, Gauge, TrendingDown, TrendingUp, Minus,
  AlertTriangle, Calendar, MapPin, Loader2, Calculator,
} from 'lucide-react';
import type { LastFuelEntry } from './expense/useLastFuelEntry';
import type { VehicleInfo } from './expense';

// ============================================================================
// PROPS
// ============================================================================

export interface FuelInsightsCardProps {
  /** Previous fuel entry for this vehicle (from getFuelEntries API) */
  lastFuelEntry: LastFuelEntry | null;
  loadingLastFuelEntry: boolean;
  /** Current form values */
  currentOdometer: number | null;
  currentLitres: number | null;
  currentAmount: number | null;
  detectedCurrency: string;
  /** Vehicle info from fleet API */
  vehicleInfo: VehicleInfo | null;
  vehicleRegistration: string | null;
  isDark: boolean;
  accent: string;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrency(amount: number, currency: string): string {
  const symbol = currency === 'ZAR' ? 'R' : currency;
  return `${symbol} ${amount.toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatNumber(n: number, decimals = 1): string {
  return n.toLocaleString('en-ZA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ============================================================================
// COMPONENT
// ============================================================================

export function FuelInsightsCard({
  lastFuelEntry,
  loadingLastFuelEntry,
  currentOdometer,
  currentLitres,
  currentAmount,
  detectedCurrency,
  vehicleInfo,
  vehicleRegistration,
  isDark,
  accent,
}: FuelInsightsCardProps) {
  // ── Derived calculations ──────────────────────────────────────────

  // Current price per litre (from form)
  const currentPricePerLitre = (currentLitres && currentLitres > 0 && currentAmount && currentAmount > 0)
    ? currentAmount / currentLitres
    : null;

  // Distance since last fill
  const distanceSinceLastFill = (lastFuelEntry?.odometerReading && currentOdometer && currentOdometer > 0)
    ? currentOdometer - lastFuelEntry.odometerReading
    : null;

  // Current fuel efficiency: L/100km = litres / distance * 100
  const currentEfficiency = (distanceSinceLastFill && distanceSinceLastFill > 0 && currentLitres && currentLitres > 0)
    ? (currentLitres / distanceSinceLastFill) * 100
    : null;

  // Previous fuel efficiency (from last entry)
  // We don't have the previous distance, so we can't calculate previous efficiency
  // unless we have the entry before the last one. For now, show the last entry's data.
  // If the last entry had litres and we can infer distance from the entry before it,
  // we'd need to fetch 2 entries. The API returns all entries, so the hook could
  // be extended. For now, we show the last entry's raw data.

  // Odometer validation: current must be >= previous
  const odometerGoingBackwards = lastFuelEntry?.odometerReading
    && currentOdometer != null
    && currentOdometer < lastFuelEntry.odometerReading
    && currentOdometer > 0;

  // Price per litre comparison
  const pricePerLitreDiff = (currentPricePerLitre && lastFuelEntry?.costPerLitre && lastFuelEntry.costPerLitre > 0)
    ? currentPricePerLitre - lastFuelEntry.costPerLitre
    : null;

  // ── Loading state ────────────────────────────────────────────────
  if (loadingLastFuelEntry) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
          <span className="text-[11px] text-gray-500 dark:text-gray-400">Loading previous fuel data…</span>
        </div>
      </div>
    );
  }

  const hasInsights = lastFuelEntry != null || currentPricePerLitre != null || distanceSinceLastFill != null || currentEfficiency != null;

  if (!hasInsights) return null;

  return (
    <div className="rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50/40 dark:bg-blue-950/10 overflow-hidden">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-blue-100 dark:border-blue-900/30">
        <Calculator className="w-3.5 h-3.5" style={{ color: accent }} />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
          Fuel Insights
        </span>
        {vehicleRegistration && (
          <span className="ml-auto text-[10px] font-mono text-gray-500 dark:text-gray-400">
            {vehicleRegistration}
          </span>
        )}
      </div>

      <div className="px-3 py-2.5 space-y-2.5">
        {/* ── Odometer validation warning ───────────────────────── */}
        {odometerGoingBackwards && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-2.5 py-2">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-red-700 dark:text-red-300">
                Odometer went backwards
              </p>
              <p className="text-[10px] text-red-600 dark:text-red-400 mt-0.5">
                Current reading ({formatNumber(currentOdometer!, 0)} km) is less than the last fill-up
                ({formatNumber(lastFuelEntry!.odometerReading, 0)} km on {formatDate(lastFuelEntry!.fillDate)}).
                Please verify the reading.
              </p>
            </div>
          </div>
        )}

        {/* ── Current fill-up metrics ──────────────────────────── */}
        <div className="grid grid-cols-2 gap-2">
          {/* Price per litre */}
          {currentPricePerLitre != null && (
            <div className="rounded-lg bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-2.5 py-2">
              <p className="text-[9px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">
                Price / Litre
              </p>
              <p className="text-sm font-bold tabular-nums text-gray-900 dark:text-white mt-0.5">
                {formatCurrency(currentPricePerLitre, detectedCurrency)}
              </p>
              {pricePerLitreDiff != null && lastFuelEntry?.costPerLitre && (
                <p className={`text-[10px] mt-0.5 flex items-center gap-0.5 ${pricePerLitreDiff > 0.01 ? 'text-red-500' : pricePerLitreDiff < -0.01 ? 'text-green-500' : 'text-gray-400'}`}>
                  {pricePerLitreDiff > 0.01 ? <TrendingUp className="w-2.5 h-2.5" /> : pricePerLitreDiff < -0.01 ? <TrendingDown className="w-2.5 h-2.5" /> : <Minus className="w-2.5 h-2.5" />}
                  {pricePerLitreDiff > 0 ? '+' : ''}{formatCurrency(pricePerLitreDiff, detectedCurrency)} vs last
                </p>
              )}
            </div>
          )}

          {/* Distance since last fill */}
          {distanceSinceLastFill != null && distanceSinceLastFill > 0 && (
            <div className="rounded-lg bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-2.5 py-2">
              <p className="text-[9px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">
                Distance
              </p>
              <p className="text-sm font-bold tabular-nums text-gray-900 dark:text-white mt-0.5">
                {formatNumber(distanceSinceLastFill, 0)} km
              </p>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                since last fill
              </p>
            </div>
          )}

          {/* Fuel efficiency (L/100km) */}
          {currentEfficiency != null && (
            <div className="rounded-lg bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-2.5 py-2">
              <p className="text-[9px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">
                Efficiency
              </p>
              <p className="text-sm font-bold tabular-nums text-gray-900 dark:text-white mt-0.5">
                {formatNumber(currentEfficiency, 1)} L/100km
              </p>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                {distanceSinceLastFill != null && currentLitres != null
                  ? `${formatNumber(currentLitres, 1)} L over ${formatNumber(distanceSinceLastFill, 0)} km`
                  : 'based on this fill'}
              </p>
            </div>
          )}

          {/* Vehicle tracking status */}
          {vehicleInfo && (
            <div className="rounded-lg bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-2.5 py-2">
              <p className="text-[9px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">
                Tracker
              </p>
              <p className="text-sm font-bold text-gray-900 dark:text-white mt-0.5 flex items-center gap-1">
                {vehicleInfo.isTracked ? (
                  <>
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    {vehicleInfo.trackingProvider || 'Live'}
                  </>
                ) : (
                  <>
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Untracked
                  </>
                )}
              </p>
              {vehicleInfo.fuelType && (
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 capitalize">
                  {vehicleInfo.fuelType.replace(/_/g, ' ')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Previous fill-up summary ─────────────────────────── */}
        {lastFuelEntry && (
          <div className="rounded-lg bg-white dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 px-2.5 py-2">
            <p className="text-[9px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold mb-1.5 flex items-center gap-1">
              <Fuel className="w-2.5 h-2.5" /> Last Fill-Up
            </p>
            <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
              <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                <Calendar className="w-2.5 h-2.5" />
                {formatDate(lastFuelEntry.fillDate)}
              </div>
              <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                <Gauge className="w-2.5 h-2.5" />
                {formatNumber(lastFuelEntry.odometerReading, 0)} km
              </div>
              {lastFuelEntry.litres > 0 && (
                <div className="text-gray-500 dark:text-gray-400">
                  {formatNumber(lastFuelEntry.litres, 1)} L
                </div>
              )}
              {lastFuelEntry.totalCost > 0 && (
                <div className="text-gray-500 dark:text-gray-400">
                  {formatCurrency(lastFuelEntry.totalCost, detectedCurrency)}
                </div>
              )}
              {lastFuelEntry.costPerLitre > 0 && (
                <div className="text-gray-500 dark:text-gray-400">
                  {formatCurrency(lastFuelEntry.costPerLitre, detectedCurrency)}/L
                </div>
              )}
              {lastFuelEntry.stationName && (
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 col-span-2 truncate">
                  <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                  {lastFuelEntry.stationName}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
